


import { Component, Emit, Prop, Ref, Vue, Watch } from "vue-property-decorator";

import { CustomError } from "App/Models";
import { Debounce, Unique, Null2Undefined } from "@Internal/Utils";
import { EventBus } from "App/IOC";

import Modal from "Web/Components/Common/Modal.vue";
import { Option } from "Web/Contracts";

import SelectOptions from "./index";

@Component({
    components: {
        Modal
    }
})
export default class GroupedAutocomplete extends Vue {
    @Prop()
    readonly Id!: string;

    @Prop()
    readonly PopupClass?: string;

    @Prop()
    readonly GetOptions!: (keyword?: string) => Promise<[Option[], CustomError | undefined]>;

    @Prop()
    readonly Options?: Option[];

    @Prop()
    SelectedOption?: Option;

    @Prop({ default: () => [] })
    SelectedOptions!: Option[];

    @Prop({ default: $t("GLOBAL_search") })
    Placeholder!: string;

    @Prop() NoResultMessage!: string;

    @Prop({ default: "" })
    Keyword!: string;

    @Prop({ default: false })
    readonly Readonly!: boolean;

    @Prop({ default: Infinity })
    readonly MaxSelections!: number;

    @Prop() readonly SelectText!: string;
    @Prop() readonly CreateText!: string;

    @Ref("container")
    private readonly container?: HTMLElement;

    @Ref("popup")
    private readonly popup?: HTMLElement;

    @Ref("options")
    private readonly options?: HTMLElement[];

    private selectedOptions: Option[] = [];

    private visibleOptions: Option[] = [];

    private keyword: string = "";

    private showOptions: boolean = false;

    private loading: boolean = false;

    private lastSelected: Option | undefined;

    private debouncedGetOptions!: (keyword?: string) => void;

    private scrollKey: number = 0;

    private id: string = "";

    private isActive: boolean = false;

    created(): void {
        this.id = Unique("grouped-autocomplete");

        this.keyword = this.Keyword;

        this.debouncedGetOptions = Debounce(this.getOptions, 300);
    }

    mounted(): void {
        EventBus.Subscribe("CloseInactiveSelectInstances", this.setInactive);
    }

    beforeDestroy(): void {
        window.removeEventListener("click", this.handleOutsideClick);
        document.removeEventListener("scroll", this.updatePopupPosition, true);
    }

    @Watch("SelectedOption", { immediate: true })
    private onSelectedOptionChange(): void {
        if(this.SelectedOption !== undefined) {
            this.selectedOptions = [this.SelectedOption];
        } else if(this.MaxSelections === 1) {
            this.selectedOptions = [];
        }
    }

    @Watch("SelectedOptions")
    private onSelectedOptionsChange(): void {
        if(this.MaxSelections > 1) {
            this.selectedOptions = this.SelectedOptions;
        }
    }

    private async getOptions(keyword: string = ""): Promise<void> {
        this.loading = true;
        this.visibleOptions = [];

        let options: Option[] = [];
        let error: CustomError | undefined;

        if(this.Options !== undefined) {
            options = this.Options;
        } else if(this.GetOptions !== undefined) {
            [options, error] = await this.GetOptions(keyword);
        }

        if(error !== undefined) {
            EventBus.Publish("Error", error);
        }

        if(!this.isActive) {
            this.loading = false;
            return;
        }

        this.visibleOptions = options;

        this.loading = false;

        this.showOptions = true;
    }

    @Watch("showOptions")
    private showOptionsChanged(shown: boolean): void {
        if(shown) {
            window.addEventListener("click", this.handleOutsideClick);
            document.addEventListener("scroll", this.updatePopupPosition, true);
            return;
        }

        window.removeEventListener("click", this.handleOutsideClick);
        document.removeEventListener("scroll", this.updatePopupPosition, true);
    }

    @Watch("keyword")
    private keywordChanged(keyword: string): void {
        if(this.keyword !== "") {
            this.debouncedGetOptions(keyword);
        }
    }

    @Emit("Select")
    private select(option: Option) {
        return option;
    }

    private async toggleOptions(value?: boolean): Promise<void> {
        if(this.Readonly) return;

        this.setActive();

        const showOptions = value !== undefined ? value : !this.showOptions;

        if(showOptions) await this.getOptions(this.keyword);

        this.showOptions = this.isActive ? showOptions : false;
    }

    private isOptionSelected(option: Option): boolean {
        return this.selectedOptions.some(selectedOption => selectedOption.Id === option.Id);
    }

    @Emit("Deselect")
    private deselect(option: Option): Option {
        return option;
    }

    private toggleSelect(option: Option): void {
        if(this.isOptionSelected(option)) {
            this.deselect(option);
        } else {
            if(this.MaxSelections === 1) {
                this.selectedOptions = [option];
            } else if(this.selectedOptions.length < this.MaxSelections) {
                this.selectedOptions.push(option);
            }
            this.select(option);
        }
    }

    private handleOutsideClick(event: Event): void {
        const popup = Null2Undefined<HTMLElement>(document.getElementById(this.Id));

        if(popup === undefined || popup.contains(event.target as HTMLElement)) return;

        this.close();

        this.setInactive();
    }

    private async close(): Promise<void> {
        this.keyword = "";

        await this.$nextTick();

        this.showOptions = false;
    }

    private reset(): void {
        this.keyword = "";
        this.visibleOptions = [];
    }

    private updatePopupPosition(event: Event): void {
        const target = event.target as HTMLElement;
        const popup = Null2Undefined<HTMLElement>(document.getElementById(this.Id));

        if(popup === undefined || popup.contains(target)) return;

        if(this.scrollKey < 500000) this.scrollKey++;
        else this.scrollKey = 0;
    }

    private create() {
        this.$emit("Create", this.keyword);

        this.close();
    }

    private setActive(): void {
        SelectOptions.Set(this.id);

        this.isActive = true;
    }

    private setInactive(excludedId?: string): void {
        if(this.id === excludedId) return;

        this.isActive = false;
        this.showOptions = false;
        this.visibleOptions = [];
    }
}
